import proj4 from 'proj4';
import { COMMON_PROJECTIONS } from '../geometry/types';

/**
 * Reproject a single coordinate [x, y] or [x, y, z]
 */
function reprojectCoordinate(
  coord: number[],
  fromProj: string,
  toProj: string
): number[] {
  if (coord.length === 2) {
    return proj4(fromProj, toProj, coord);
  } else if (coord.length >= 3) {
    const [x, y] = proj4(fromProj, toProj, [coord[0], coord[1]]);
    return [x, y, coord[2]]; // Preserve Z coordinate
  }
  return coord;
}

/**
 * Reproject coordinates array (for LineString)
 */
function reprojectCoordinates(
  coords: number[][],
  fromProj: string,
  toProj: string
): number[][] {
  return coords.map(coord => reprojectCoordinate(coord, fromProj, toProj));
}

/**
 * Reproject polygon rings (outer ring + holes)
 */
function reprojectPolygonCoordinates(
  rings: number[][][],
  fromProj: string,
  toProj: string
): number[][][] {
  return rings.map(ring => reprojectCoordinates(ring, fromProj, toProj));
}

/**
 * Reproject a single geometry
 */
function reprojectGeometry(
  geometry: any,
  fromProj: string,
  toProj: string
): any {
  if (!geometry || !geometry.type) {
    return geometry;
  }
  
  const newGeometry = { ...geometry };
  
  switch (geometry.type) {
    case 'Point':
      newGeometry.coordinates = reprojectCoordinate(
        geometry.coordinates,
        fromProj,
        toProj
      );
      break;
      
    case 'MultiPoint':
    case 'LineString':
      newGeometry.coordinates = reprojectCoordinates(
        geometry.coordinates,
        fromProj,
        toProj
      );
      break;
      
    case 'MultiLineString':
    case 'Polygon':
      newGeometry.coordinates = reprojectPolygonCoordinates(
        geometry.coordinates,
        fromProj,
        toProj
      );
      break;
      
    case 'MultiPolygon':
      newGeometry.coordinates = geometry.coordinates.map((polygon: number[][][]) =>
        reprojectPolygonCoordinates(polygon, fromProj, toProj)
      );
      break;
      
    case 'GeometryCollection':
      newGeometry.geometries = geometry.geometries.map((geom: any) =>
        reprojectGeometry(geom, fromProj, toProj)
      );
      break;
      
    default:
      // Unknown geometry type - pass through unchanged
  }
  
  return newGeometry;
}

/**
 * Reproject entire GeoJSON FeatureCollection
 */
export function reprojectGeoJSON(
  geojson: any,
  fromEPSG: number,
  toEPSG: number
): any {
  // Get projection definitions
  const fromProjection = COMMON_PROJECTIONS.find(p => p.epsg === fromEPSG);
  const toProjection = COMMON_PROJECTIONS.find(p => p.epsg === toEPSG);
  
  if (!fromProjection?.proj4) {
    throw new Error(`Projection definition not found for EPSG:${fromEPSG}`);
  }
  
  if (!toProjection?.proj4) {
    throw new Error(`Projection definition not found for EPSG:${toEPSG}`);
  }
  
  const fromProj = fromProjection.proj4;
  const toProj = toProjection.proj4;
  
  // Create new GeoJSON with reprojected features
  const reprojected = {
    type: 'FeatureCollection',
    features: geojson.features.map((feature: any) => ({
      ...feature,
      geometry: reprojectGeometry(feature.geometry, fromProj, toProj)
    })),
    crs: {
      type: 'name',
      properties: {
        name: `EPSG:${toEPSG}`
      }
    }
  };
  
  return reprojected;
}

/**
 * Detect geometry types in GeoJSON
 */
export function detectGeometryTypes(geojson: any): Set<string> {
  const types = new Set<string>();
  
  if (geojson.features) {
    geojson.features.forEach((feature: any) => {
      if (feature.geometry && feature.geometry.type) {
        types.add(feature.geometry.type);
      }
    });
  }
  
  return types;
}

/**
 * Get feature count by geometry type
 */
export function getFeatureStats(geojson: any): Record<string, number> {
  const stats: Record<string, number> = {};
  
  if (geojson.features) {
    geojson.features.forEach((feature: any) => {
      const type = feature.geometry?.type || 'Unknown';
      stats[type] = (stats[type] || 0) + 1;
    });
  }
  
  return stats;
}
